import React, { useEffect, useState } from 'react';
import { Monitor, Apple, HardDriveDownload, ShieldAlert, Lock, FolderSearch, Factory, LayoutDashboard, LogIn, Globe } from 'lucide-react';
import PageHeader from './common/PageHeader';
import OpenAgentSettingsButton from './common/OpenAgentSettingsButton';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { isAgentOutdated, LATEST_AGENT_BUILD } from '../config/agentVersion';
import { getFeatures, isClinicScanIntakeOn } from '../utils/features';

// 덴클 프로그램(에이전트) 다운로드 페이지 — 2026-10-25 대표 지시로 전면 재구성
//   구성: ① 상단 다운로드 ② 중간 기능(캡처 + 단어만) ③ 하단 사용 방법(캡처 + 단어만). 설명문은 넣지 않는다.
//   지금 쓰는 기능만: 거래명세서 수집 · 제작 공정 반영 · 지그·3D 모델. (재택·CAD 디자인 외주는 10-25 숨김 — 여기서도 뺀다)
//   캡처: public/images/agent/*.png — dentcluster-agent-v2 renderer 를 Electron 으로 480×820 에서 찍은 것(샘플 데이터, 실명 0).
// 설치 파일은 Firebase Storage /agents/ 에 업로드 (공개 읽기). 1.0 단일판.
const STORAGE_BASE = 'https://firebasestorage.googleapis.com/v0/b/dental-lab-system.firebasestorage.app/o';
const dl = (name) => `${STORAGE_BASE}/${encodeURIComponent(`agents/${name}`)}?alt=media`;
const DOWNLOADS = {
    windows: dl('DentClusterAgent2-Setup-1.0.0-alpha.123.exe'),
    mac: dl('DentClusterAgent2-1.0.0-alpha.123.dmg'),
};
const IMG = (name) => `${process.env.PUBLIC_URL || ''}/images/agent/${name}.png`;

// ② 기능 — 화면 하나에 단어만 (대표 10-25 "핵심기능만 설명없이 단어만으로")
const FEATURES = [
    {
        key: 'stmt', title: '거래명세서 수집', icon: FolderSearch, color: '#059669', bg: '#ecfdf5', img: 'page-stmt',
        words: ['CAD 폴더 연결', 'exocad · 3Shape', '5분마다 자동', '환자 · 치과 · 치식 · 재료', '월말 명세서 자동 채움', '제외 · 복원'],
    },
    {
        key: 'jig', title: '지그 · 3D 모델', icon: Factory, color: '#6d28d9', bg: '#f5f3ff', img: 'page-jig',
        words: ['커스텀 어버트먼트 케이스', '후보 자동 탐색', '최근 1 · 3 · 7 · 14일', '환자 검색', '선택분 생성', '지그 STL · 3D 모델 STL', '실패 → 다시 제작', '웹에서 내려받기'],
    },
    {
        key: 'dash', title: '제작 공정 · 상태', icon: LayoutDashboard, color: '#2563eb', bg: '#eff6ff', img: 'page-dash',
        words: ['의뢰서 확인 → 디자인 완료', '자동 반영', '마지막 수집', '서버 보고', '메모리 · 실행 횟수', '활동 기록'],
    },
];

// ③ 사용 방법 — 순서대로, 단어만
const STEPS = [
    { n: 1, title: '설치 · 로그인', icon: LogIn, img: 'login', words: ['Windows .exe / Mac .dmg', '덴탈클러스터 계정', '자동 로그인'] },
    { n: 2, title: 'CAD 폴더 연결', icon: FolderSearch, img: 'page-stmt', words: ['[+ CAD 작업 폴더 연결]', '작업 폴더 전체', '켜기', '[이 기간 수집하기]'] },
    { n: 3, title: '지그 · 3D 모델', icon: Factory, img: 'page-jig', words: ['[+ 제작 폴더 연결]', '켜기', '체크 → [선택분 생성]', '지그 / 모델 선택'] },
    { n: 4, title: '웹에서 받기', icon: Globe, img: 'page-account', words: ['덴클 웹 › 지그·3D 모델', '거래명세서 작성', '14일 안 다시 받기'] },
];

function AgentDownload({ user }) {
    const isMac = /Mac/i.test(navigator.platform);

    const [features, setFeatures] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatures(f); });
        return () => { alive = false; };
    }, []);
    // 🔔 설치된 PC 중 구버전이 있으면 재설치 안내 (대표 지시 2026-08-25 — 사이드메뉴 '업데이트' 배지와 동일 판정)
    const [outdatedVersions, setOutdatedVersions] = useState([]);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const tid = user?.companyId || user?.uid;
                if (!tid) return;
                const snap = await getDoc(doc(db, 'agentStatus', tid));
                if (!alive || !snap.exists()) return;
                const cutoff = Date.now() - 30 * 86400000;
                const olds = Object.values(snap.data().devices || {})
                    .filter(d => (d?.lastSeen?.toMillis ? d.lastSeen.toMillis() : 0) > cutoff && isAgentOutdated(d?.version))
                    .map(d => d.version);
                if (alive) setOutdatedVersions([...new Set(olds)]);
            } catch (e) { console.warn('에이전트 버전 확인 실패(안내 생략):', e?.message); }
        })();
        return () => { alive = false; };
    }, [user?.uid, user?.companyId]);

    // 기공소=전체 오픈, 치과=isClinicScanIntakeOn(베타 판정 포함) — 종전 판정 그대로
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = ['clinic', 'dental', '치과'].includes(businessType);
    const allowed = isClinic ? isClinicScanIntakeOn(features, user, auth.currentUser?.email) : true;
    if (!features && !allowed) return null;

    if (!allowed) {
        return (
            <div style={{ ...styles.container, textAlign: 'center', padding: '80px 20px' }}>
                <Lock size={40} color="#94a3b8" />
                <div style={{ fontSize: 18, fontWeight: 800, color: '#334155', marginTop: 16 }}>준비 중인 기능입니다</div>
                <div style={{ fontSize: 14, color: '#64748b', marginTop: 8 }}>덴클 프로그램은 현재 일부 사용자에게만 제공되고 있습니다.</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <PageHeader icon={HardDriveDownload} title="덴클 프로그램 다운로드" />

            {outdatedVersions.length > 0 && (
                <div style={styles.outdated}>
                    <b>새 버전이 나왔습니다.</b> 이 계정의 PC에 구버전({outdatedVersions.join(', ')})이 설치되어 있습니다. 아래에서 새로 받아 설치하세요.
                </div>
            )}

            {/* ① 상단 — 다운로드 */}
            <div style={styles.hero}>
                <div style={styles.heroTitle}>덴클 프로그램</div>
                <div style={styles.heroWords}>
                    {['거래명세서 수집', '제작 공정 반영', '지그 · 3D 모델'].map(w => <span key={w} style={styles.heroChip}>{w}</span>)}
                </div>
                <div style={styles.buttonRow}>
                    <a href={DOWNLOADS.windows} style={{ ...styles.dlButton, ...(isMac ? styles.dlButtonSecondary : {}) }}>
                        <Monitor size={18} /> Windows 다운로드 (.exe)
                    </a>
                    <a href={DOWNLOADS.mac} style={{ ...styles.dlButton, ...(isMac ? {} : styles.dlButtonSecondary) }}>
                        <Apple size={18} /> Mac 다운로드 (.dmg)
                    </a>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>버전 {LATEST_AGENT_BUILD} · Windows는 자동 업데이트 · Mac은 다시 설치</div>
                <details style={styles.warnBox}>
                    <summary style={styles.warnSummary}><ShieldAlert size={15} /> 설치 시 보안 경고가 뜨면</summary>
                    <ul style={{ margin: '8px 0 0 18px', lineHeight: 1.8, fontSize: 13, color: '#78350f' }}>
                        <li><b>Windows</b>: SmartScreen → <b>추가 정보 → 실행</b></li>
                        <li><b>Mac</b>: "손상되어 열 수 없습니다" → 터미널 <code style={styles.code}>xattr -cr "/Applications/DentClusterAgent2.app"</code> 후 다시 열기</li>
                    </ul>
                </details>
            </div>

            {/* 이미 설치한 사용자 — [에이전트 설정 열기] (온라인 PC 자동 지목) */}
            {(user?.companyId || user?.uid) && (
                <div style={{ ...styles.card, marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>이미 설치하셨나요?</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 4 }}>켜져 있는 PC의 프로그램 설정 화면을 엽니다.</div>
                    </div>
                    <OpenAgentSettingsButton labId={user?.companyId || user?.uid} section="cad" requestedBy={user?.uid} />
                </div>
            )}

            {/* ② 중간 — 기능 (캡처 + 단어) */}
            <div style={styles.sectionTitle}>기능</div>
            <div style={styles.featureGrid}>
                {FEATURES.map(f => {
                    const Icon = f.icon;
                    return (
                        <div key={f.key} style={styles.card}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                <span style={{ ...styles.iconWrap, background: f.bg, color: f.color }}><Icon size={16} /></span>
                                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{f.title}</span>
                            </div>
                            <img src={IMG(f.img)} alt={f.title} style={styles.shot} loading="lazy" />
                            <div style={styles.wordWrap}>
                                {f.words.map(w => <span key={w} style={{ ...styles.chip, borderColor: f.bg, background: f.bg, color: f.color }}>{w}</span>)}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ③ 하단 — 사용 방법 (순서 + 캡처 + 단어) */}
            <div style={styles.sectionTitle}>사용 방법</div>
            <div style={styles.stepGrid}>
                {STEPS.map(s => {
                    const Icon = s.icon;
                    return (
                        <div key={s.n} style={styles.card}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                <span style={styles.stepNo}>{s.n}</span>
                                <Icon size={15} color="#6366f1" />
                                <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{s.title}</span>
                            </div>
                            <img src={IMG(s.img)} alt={s.title} style={styles.shotSmall} loading="lazy" />
                            <div style={styles.wordWrap}>
                                {s.words.map(w => <span key={w} style={styles.chip}>{w}</span>)}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div style={{ fontSize: 13, color: '#64748b', marginTop: 24, textAlign: 'center' }}>
                문의: 010-2972-3733 · 카카오톡 pf.kakao.com/_xfsREX
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: 880, margin: '0 auto', padding: '0 16px 40px' },
    outdated: { background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 16px', marginBottom: 14, fontSize: 13.5, color: '#92400E', lineHeight: 1.7 },
    hero: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '30px 24px 18px', marginTop: 16,
    },
    heroTitle: { fontSize: 22, fontWeight: 800, color: '#0f172a' },
    heroWords: { display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 10 },
    heroChip: { fontSize: 13, fontWeight: 700, color: '#4338ca', background: '#eef2ff', borderRadius: 999, padding: '4px 12px' },
    buttonRow: { display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap', justifyContent: 'center' },
    dlButton: {
        display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#6366f1', color: '#ffffff', textDecoration: 'none',
        borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 700,
    },
    dlButtonSecondary: { backgroundColor: '#f1f5f9', color: '#475569' },
    warnBox: { width: '100%', textAlign: 'left', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginTop: 16 },
    warnSummary: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 700, color: '#92400e', fontSize: 13.5, listStyle: 'none' },
    code: { background: '#fde68a', padding: '1px 6px', borderRadius: 4 },
    sectionTitle: { fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '28px 0 12px' },
    featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 },
    stepGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 },
    card: { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 },
    iconWrap: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8 },
    shot: { width: '100%', height: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', display: 'block', background: '#f8fafc' },
    shotSmall: { width: '100%', height: 220, objectFit: 'cover', objectPosition: 'top', borderRadius: 10, border: '1px solid #e2e8f0', display: 'block', background: '#f8fafc' },
    wordWrap: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
    chip: { fontSize: 12.5, fontWeight: 700, color: '#334155', background: '#f1f5f9', border: '1px solid #f1f5f9', borderRadius: 8, padding: '4px 9px', whiteSpace: 'nowrap' },
    stepNo: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: '#6366f1', color: '#fff', fontSize: 12, fontWeight: 800 },
};

export default AgentDownload;
